import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { QuickCapture } from './QuickCapture'
import { isBareShortcut } from '@/lib/hotkeys'

/** The key that opens it. Bare, because a shortcut with a modifier is slower. */
export const CAPTURE_KEY = 'c'

/**
 * Capture from anywhere, without losing your place.
 *
 * The whole value of quick capture is that the thought goes down before it
 * goes away, and navigating to the inbox first is exactly the friction that
 * loses it. So one key opens a field over whatever you were looking at, and
 * Escape puts it back.
 *
 * It stays open after each capture, cleared and ready. Brain-dumps arrive in
 * threes, and closing after the first one makes you press the key again for
 * the other two.
 */
/**
 * Whether the capture dialog is open, in a store rather than in the component.
 *
 * Same reason as search: the dialog renders nothing while closed, so a phone —
 * which has no `c` key to press — had no way to capture from anywhere except
 * Today, where the inline field lives. The page heading opens it now.
 */
export const useCaptureOpen = create<{
  open: boolean
  setOpen: (open: boolean) => void
}>((set) => ({ open: false, setOpen: (open) => set({ open }) }))

export function CaptureDialog() {
  const open = useCaptureOpen((state) => state.open)
  const setOpen = useCaptureOpen((state) => state.setOpen)
  const [captured, setCaptured] = useState(0)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (open && event.key === 'Escape') {
        setOpen(false)
        return
      }
      if (!open && isBareShortcut(event, CAPTURE_KEY)) {
        // Otherwise the `c` that opened the dialog is also typed into it.
        event.preventDefault()
        setCaptured(0)
        setOpen(true)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, setOpen])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Capture"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/20 px-4 pt-[15vh]"
      // Clicking away is the same as Escape: nothing here is a form you can
      // half-fill, so there is nothing to lose by closing it.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setOpen(false)
      }}
    >
      <div className="bg-background w-full max-w-lg rounded-lg p-3 shadow-lg">
        <QuickCapture autoFocus onCaptured={() => setCaptured(captured + 1)} />
        <p className="text-muted-foreground mt-2 text-xs">
          {captured === 0
            ? 'Enter to add · Esc to close'
            : `${captured} captured — keep going, or Esc to close`}
        </p>
      </div>
    </div>
  )
}
